import { useEffect, useState } from 'react';
import { Box, Button, Card, Center, Group, Image, List, Loader, Stack, Text, Title } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { decideConsent, fetchConsent, type ConsentDetails } from '@/api/auth';
import { useAuth } from '@/auth/useAuth';
import { usePageTitle } from '@/components/usePageTitle';
import { forest } from '@/theme/theme';

export const Route = createFileRoute('/oauth/consent')({
  validateSearch: (s: Record<string, unknown>): { req: string } => ({ req: typeof s.req === 'string' ? s.req : '' }),
  component: ConsentRoute,
});

function ConsentRoute() {
  const { req } = Route.useSearch();
  return <ConsentPage req={req} />;
}

/** The OAuth consent screen: an MCP host asks to act as the signed-in user (docs/mcp-api.md §2.2). */
export function ConsentPage({ req }: { req: string }) {
  const { t } = useTranslation();
  usePageTitle(t('auth.consentTitle'));
  const { me } = useAuth();
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [invalid, setInvalid] = useState(false);
  const [deciding, setDeciding] = useState<'allow' | 'deny' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchConsent(req)
      .then((d) => { if (!cancelled) setDetails(d); })
      .catch(() => { if (!cancelled) setInvalid(true); });
    return () => { cancelled = true; };
  }, [req]);

  const decide = async (allow: boolean) => {
    setError(null);
    setDeciding(allow ? 'allow' : 'deny');
    try {
      window.location.assign(await decideConsent(req, allow));
    } catch {
      setError(t('auth.genericError'));
      setDeciding(null);
    }
  };

  const clientName = details?.client.name || details?.client.public_id || '';
  return (
    <Box pos="relative" h="100vh" bg={forest[9]} style={{ overflow: 'hidden' }}>
      <Box
        pos="absolute"
        inset={0}
        style={{ backgroundImage: 'url(/rastr.png)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.28 }}
      />
      <Center pos="relative" h="100%">
        <Stack align="center" gap="xl">
          <Image src="/logo-white.png" alt="Forestum" w={160} fit="contain" />
          <Card w={420}>
            {invalid ? (
              <Text role="alert">{t('auth.consentInvalid')}</Text>
            ) : !details ? (
              <Center py="md">
                <Loader color="forest" />
              </Center>
            ) : (
              <Stack gap="md">
                <Title order={3}>{t('auth.consentTitle')}</Title>
                <Text>{t('auth.consentIntro', { client: clientName, email: me?.email ?? details.user.email })}</Text>
                <List spacing="xs">
                  {details.scopes.map((s) => (
                    <List.Item key={s}>{t(`auth.scope_${s}`)}</List.Item>
                  ))}
                </List>
                {details.narrowed && <Text size="sm">{t('auth.consentNarrowed')}</Text>}
                <Text size="sm">{t('auth.consentRedirect', { host: details.redirect_host })}</Text>
                {error && (
                  <Text c="red" role="alert">
                    {error}
                  </Text>
                )}
                <Group justify="flex-end">
                  <Button variant="default" onClick={() => void decide(false)} loading={deciding === 'deny'} disabled={deciding === 'allow'}>
                    {t('auth.consentDeny')}
                  </Button>
                  <Button onClick={() => void decide(true)} loading={deciding === 'allow'} disabled={deciding === 'deny'}>
                    {t('auth.consentAllow')}
                  </Button>
                </Group>
              </Stack>
            )}
          </Card>
        </Stack>
      </Center>
    </Box>
  );
}
